import { useState, useEffect, useMemo } from 'react';
import apiAxiosInstance from '../utils/axiosInstance';

export interface Teacher {
  teachingStandards?: any[];
  [key: string]: any;
}

export interface UseTeacherStandardReturn {
  standardName: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to extract and fetch the teacher's teaching standard name.
 * Handles both populated objects and ObjectId strings.
 * 
 * @param teacher - The teacher object from context
 * @returns Object containing standardName, loading state, and error state
 */
export const useTeacherStandard = (teacher: Teacher | null): UseTeacherStandardReturn => {
  const [standardName, setStandardName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Extract the standard ID or name from teachingStandards
  const standardInfo = useMemo(() => {
    if (!teacher?.teachingStandards || !Array.isArray(teacher.teachingStandards) || teacher.teachingStandards.length === 0) {
      return { type: 'none' as const };
    }
    
    // Use the first element from the array
    const firstStandard = teacher.teachingStandards[0];
    
    // If it's already a populated object with name, return it
    if (typeof firstStandard === 'object' && firstStandard !== null && 'name' in firstStandard && firstStandard.name) {
      return { type: 'populated' as const, name: String(firstStandard.name) };
    }
    
    // If it's an ObjectId string, return the ID to fetch
    if (typeof firstStandard === 'string' && firstStandard.length === 24) {
      return { type: 'objectId' as const, id: firstStandard };
    }
    
    // Unknown format
    return { type: 'invalid' as const };
  }, [teacher?.teachingStandards]);
  
  // Set standardName immediately if already populated
  useEffect(() => {
    if (standardInfo.type === 'populated') {
      setStandardName(standardInfo.name);
      setLoading(false);
      setError(null);
    } else if (standardInfo.type === 'none') {
      setStandardName(null);
      setLoading(false);
      setError(null);
    } else if (standardInfo.type === 'invalid') {
      setStandardName(null);
      setLoading(false);
      setError('Invalid teaching standard format');
      console.warn('⚠️ Invalid teachingStandards format:', teacher?.teachingStandards?.[0]);
    }
  }, [standardInfo, teacher?.teachingStandards]);
  
  // Fetch standard name if it's an ObjectId
  useEffect(() => {
    if (standardInfo.type !== 'objectId') {
      return;
    }
    
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    
    console.log('🔍 [useTeacherStandard] Fetching Standard name for ObjectId:', standardInfo.id);
    
    apiAxiosInstance.get(`/api/standards/${standardInfo.id}`, {
      signal: controller.signal
    })
      .then(response => {
        if (response.data?.name) {
          console.log('✅ [useTeacherStandard] Fetched Standard name:', response.data.name);
          setStandardName(String(response.data.name));
          setError(null);
        } else {
          console.warn('⚠️ [useTeacherStandard] Standard fetched but no name field:', response.data);
          setStandardName(null);
          setError('Teaching standard has no name');
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('❌ [useTeacherStandard] Error fetching Standard:', err);
          setStandardName(null);
          setError('Failed to load teaching standard');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
    
    return () => {
      controller.abort();
    };
  }, [standardInfo]);
  
  return { standardName, loading, error };
};

